import { createAction, createReducer } from 'deox';
import { monthlyPayment } from 'utils/finance';

import { resetApplication } from './resetApplication';
import { filter, propEq } from 'ramda';
import { addDays } from 'date-fns/fp';

interface BaseFinancingOffer {
  id: string;
  status: string;
  statusCodes: string[];
  paymentProductId: string;
  capacityValue: number;
  paymentAgreementId: string;
  paymentAgreementStatus?: 'OFFERED';
  originalPrincipalValue: number;
  termLength: number;
  termRate: number;
  createdAt: string;
  expiresAt: string;

  paymentProductType: 'SPLITPAY' | 'INSTALLMENTS';
  termInterval: 'BIWEEKLY' | 'MONTHLY';
}

export interface InstallmentsOffer extends BaseFinancingOffer {
  paymentProductType: 'INSTALLMENTS';
  termInterval: 'MONTHLY';
}

export interface SplitPayOffer extends BaseFinancingOffer {
  paymentProductType: 'SPLITPAY';
  termInterval: 'BIWEEKLY';
}

export type FinanceOffer = InstallmentsOffer | SplitPayOffer;

export type ApplicationStatus =
  | 'APPROVED'
  | 'STARTED'
  | 'INELIGIBLE'
  | 'NEEDS_ACTION'
  | 'DENIED';

export type FinanceResult = {
  id: string;
  buyerId: string;
  status: ApplicationStatus;
  statusCodes: string[];
  offers: FinanceOffer[];
};

export interface FinanceOffersState {
  entity: FinanceResult | null;
}

export const defaultState: FinanceOffersState = {
  entity: null,
};

export const setFinanceOffers = createAction(
  'SET_FINANCE_OFFERS',
  resolve => (financeResult: FinanceResult) => resolve({ financeResult }),
);

export const FinanceResultReducer = createReducer(
  defaultState,
  handleAction => [
    handleAction(setFinanceOffers, (state, { payload: { financeResult } }) => ({
      ...state,
      entity: financeResult,
    })),

    handleAction(resetApplication, () => defaultState),
  ],
);

export interface MountedState {
  financeOffers: FinanceOffersState;
}

export const selectFinanceOffers = (
  state: MountedState,
): FinanceOffer[] | null | undefined =>
  (state.financeOffers.entity || {}).offers;

export const selectFinanceResult = (
  state: MountedState,
): FinanceResult | null => state.financeOffers.entity;

export const selectFinanceOffersByType = (state: MountedState) => {
  const offers = selectFinanceOffers(state);
  const installmentOffers = filter(
    propEq('paymentProductType', 'INSTALLMENTS'),
    offers || [],
  ) as InstallmentsOffer[];

  const splitPayOffers = filter(
    propEq('paymentProductType', 'SPLITPAY'),
    offers || [],
  ) as SplitPayOffer[];

  return {
    installmentOffers,
    splitPayOffers,
  };
};

export const selectFinanceOffersStatus = (
  state: MountedState,
): ApplicationStatus | null | undefined =>
  (state.financeOffers.entity || {}).status;

export interface InstallmentOfferData {
  id: string;
  perMonth: number;
  termLength: number;
  apr: number;
  total: number;
  interest: number;
  capacityValue: number;
  firstPaymentDate: Date;
}

export const getInstallmentOfferData = (
  principal: number,
  installmentOffer: InstallmentsOffer,
  startDate?: Date,
): InstallmentOfferData => {
  const apr = installmentOffer.termRate / 100;
  const termLength = installmentOffer.termLength;
  const perMonth = monthlyPayment(principal, apr, termLength);
  const total = perMonth * termLength;
  const interest = total - principal;
  startDate = startDate || new Date();
  const firstPaymentDate = addDays(30, startDate);
  return {
    id: installmentOffer.id,
    perMonth,
    termLength,
    apr,
    total,
    interest,
    firstPaymentDate,
    capacityValue: installmentOffer.capacityValue,
  };
};

export interface SplitPayOfferData {
  id: string;
  principal: number;
  capacityValue: number;
  biweeklyPayment: number;
  termLength: number;
}

export const getSplitPayOfferData = (
  principal: number,
  offer: SplitPayOffer,
): SplitPayOfferData => {
  const termLength = offer.termLength;
  const biweeklyPayment = principal / termLength;
  return {
    id: offer.id,
    principal: principal,
    capacityValue: offer.capacityValue,
    biweeklyPayment,
    termLength,
  };
};

export default FinanceResultReducer;
